import React from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Lock, LogOut, Mail, Building2, MapPin, MessageSquare, Loader2,
  CheckCircle2, Archive, Trash2, RefreshCw, ArrowLeft, Inbox, Search, Download,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "hx_admin_token";

export default function AdminPage() {
  const [token, setToken] = React.useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = React.useState(!!sessionStorage.getItem(TOKEN_KEY));

  if (!authed) {
    return <LoginScreen onAuth={(t) => { sessionStorage.setItem(TOKEN_KEY, t); setToken(t); setAuthed(true); }} />;
  }
  return <Dashboard token={token} onLogout={() => { sessionStorage.removeItem(TOKEN_KEY); setAuthed(false); setToken(""); }} />;
}

// ===== LOGIN =====
const LoginScreen = ({ onAuth }) => {
  const [val, setVal] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!val.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { token: val.trim() });
      onAuth(val.trim());
      toast.success("Welcome to HX Admin");
    } catch (err) {
      toast.error(err.response?.status === 403 ? "Invalid admin token" : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden" data-testid="admin-login">
      <div className="absolute inset-0 hx-grid-bg opacity-25" />
      <div className="absolute inset-0 hx-radial-spot" />
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        onSubmit={submit}
        className="relative w-full max-w-md hx-glass-strong rounded-2xl p-8 lg:p-10"
      >
        <Link to="/" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-hx-muted hover:text-hx-neon transition-colors mb-8">
          <ArrowLeft size={12} /> Back to site
        </Link>

        <div className="w-12 h-12 rounded-md hx-metal flex items-center justify-center mb-6">
          <Lock size={18} className="text-hx-neon" strokeWidth={1.5} />
        </div>

        <div className="text-[10px] uppercase tracking-[0.28em] text-hx-muted">HX Ecosystems</div>
        <h1 className="mt-3 font-display font-bold text-3xl lg:text-4xl tracking-tight">
          <span className="hx-neon-text">Admin</span> Console
        </h1>
        <p className="mt-3 text-sm text-hx-muted">
          Restricted access · Strategic contact management.
        </p>

        <div className="mt-8">
          <label className="text-[10px] uppercase tracking-[0.22em] text-hx-muted mb-2 block">
            Admin Token
          </label>
          <input
            type="password"
            data-testid="admin-token-input"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="••••••••••••"
            autoFocus
            className="w-full px-4 py-3 rounded-md bg-white/[0.03] border border-white/[0.10] text-hx-text outline-none transition-all focus:border-hx-neon/40 focus:bg-hx-neon/[0.03] focus:shadow-[0_0_0_3px_rgba(10,155,95,0.08)]"
          />
        </div>

        <button
          type="submit"
          disabled={loading || !val.trim()}
          data-testid="admin-login-button"
          className="mt-6 btn-hx-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? <Loader2 className="animate-spin" size={16} /> : <Lock size={14} />}
          {loading ? "Authenticating…" : "Access Console"}
        </button>

        <div className="mt-8 pt-6 border-t border-white/[0.10] text-[10px] uppercase tracking-[0.22em] text-hx-muted">
          Integrated & Operated by BSGOTECH Infrastructure Systems
        </div>
      </motion.form>
    </div>
  );
};

// ===== DASHBOARD =====
const Dashboard = ({ token, onLogout }) => {
  const [contacts, setContacts] = React.useState([]);
  const [stats, setStats] = React.useState({ total: 0, new: 0, contacted: 0, archived: 0 });
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState("all");
  const [query, setQuery] = React.useState("");
  const [active, setActive] = React.useState(null);

  const headers = React.useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/contacts`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setContacts(cRes.data);
      setStats(sRes.data);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error("Session expired");
        onLogout();
      } else {
        toast.error("Failed to load contacts");
      }
    } finally {
      setLoading(false);
    }
  }, [headers, onLogout]);

  React.useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/contacts/${id}`, { status }, { headers });
      toast.success(`Marked as ${status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this contact permanently?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { headers });
      toast.success("Contact deleted");
      setActive(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const [exporting, setExporting] = React.useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const res = await axios.get(`${API}/admin/contacts/export`, {
        headers,
        params,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 16).replace(/[:T]/g, "-");
      a.href = url;
      a.download = `hannonexus-contacts-${filter}-${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error("Session expired");
        onLogout();
      } else {
        toast.error("Export failed");
      }
    } finally {
      setExporting(false);
    }
  };

  const filtered = contacts.filter((c) => {
    const s = c.status || "new";
    if (filter !== "all" && s !== filter) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      c.name?.toLowerCase().includes(q) ||
      c.company?.toLowerCase().includes(q) ||
      c.country?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.message?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen relative" data-testid="admin-dashboard">
      <div className="absolute inset-0 hx-grid-bg opacity-20 pointer-events-none" />

      {/* Header */}
      <header className="relative border-b border-white/[0.10] hx-glass">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-md hx-metal flex items-center justify-center">
              <span className="font-display font-bold text-hx-neon text-xs">HX</span>
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold tracking-tight text-sm">HX Admin</div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-hx-muted">Strategic Contacts</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="text-hx-muted hover:text-hx-neon transition-colors p-2"
              aria-label="Refresh"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="text-[11px] uppercase tracking-[0.22em] text-hx-muted hover:text-hx-text transition-colors inline-flex items-center gap-2"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="relative max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-10">
          <StatCard label="Total" value={stats.total} testid="stat-total" />
          <StatCard label="New" value={stats.new} accent testid="stat-new" />
          <StatCard label="Contacted" value={stats.contacted} testid="stat-contacted" />
          <StatCard label="Archived" value={stats.archived} muted testid="stat-archived" />
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-end justify-between mb-6">
          <div className="flex items-center gap-x-6 border-b border-white/[0.10] pb-2 overflow-x-auto">
            {[
              ["all", "All"],
              ["new", "New"],
              ["contacted", "Contacted"],
              ["archived", "Archived"],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                data-testid={`filter-${key}`}
                className={`relative text-[11px] uppercase tracking-[0.22em] pb-2 transition-colors shrink-0 ${
                  filter === key ? "text-hx-neon" : "text-hx-muted hover:text-hx-text"
                }`}
              >
                {label}
                {filter === key && <span className="absolute left-0 right-0 -bottom-[2px] h-[2px] bg-hx-neon" />}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-72">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-hx-muted" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search company, name, country…"
                data-testid="admin-search"
                className="w-full pl-9 pr-3 py-2 text-sm rounded-md bg-white/[0.03] border border-white/[0.10] text-hx-text outline-none focus:border-hx-neon/40"
              />
            </div>
            <button
              type="button"
              onClick={exportCsv}
              disabled={exporting || loading}
              data-testid="admin-export-csv"
              title={filter === "all" ? "Export all contacts as CSV" : `Export ${filter} contacts as CSV`}
              className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-md border border-white/[0.10] bg-white/[0.03] text-[11px] uppercase tracking-[0.22em] text-hx-muted hover:text-hx-neon hover:border-hx-neon/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
              <span className="hidden sm:inline">Export CSV</span>
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="hx-glass-strong rounded-2xl overflow-hidden" data-testid="contacts-table">
          {loading ? (
            <div className="py-20 flex items-center justify-center text-hx-muted">
              <Loader2 className="animate-spin mr-2" size={16} /> Loading contacts…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 flex flex-col items-center justify-center text-hx-muted">
              <Inbox size={32} className="mb-3 opacity-50" strokeWidth={1.2} />
              <div className="text-sm">No contacts match the current filter.</div>
            </div>
          ) : (
            <div className="divide-y divide-white/[0.05]">
              {filtered.map((c) => (
                <ContactRow
                  key={c.id}
                  contact={c}
                  onClick={() => setActive(c)}
                  onMark={updateStatus}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Detail Drawer */}
      <AnimatePresence>
        {active && (
          <DetailDrawer
            contact={active}
            onClose={() => setActive(null)}
            onUpdate={updateStatus}
            onDelete={remove}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const StatCard = ({ label, value, accent, muted, testid }) => (
  <div
    data-testid={testid}
    className={`rounded-xl px-5 py-5 lg:py-6 ${accent ? "hx-glass-strong hx-glow-soft" : "hx-glass"}`}
  >
    <div className="text-[10px] uppercase tracking-[0.24em] text-hx-muted">{label}</div>
    <div className={`mt-2 font-display font-bold text-3xl lg:text-4xl tabular-nums tracking-tight ${
      accent ? "hx-neon-text" : muted ? "text-hx-muted" : "text-hx-text"
    }`}>
      {value}
    </div>
  </div>
);

const STATUS_BADGE = {
  new: { label: "NEW", cls: "text-hx-neon border-hx-neon/30 bg-hx-neon/[0.06]" },
  contacted: { label: "CONTACTED", cls: "text-hx-text border-white/15 bg-white/[0.03]" },
  archived: { label: "ARCHIVED", cls: "text-hx-muted border-white/[0.10] bg-white/[0.03]" },
};

const ContactRow = ({ contact, onClick, onMark }) => {
  const s = contact.status || "new";
  const badge = STATUS_BADGE[s] || STATUS_BADGE.new;
  return (
    <div
      data-testid={`contact-row-${contact.id}`}
      className="px-5 lg:px-7 py-5 hover:bg-white/[0.03] transition-colors cursor-pointer group flex items-center gap-5"
      onClick={onClick}
    >
      <div className="hidden sm:block w-1 h-12 rounded-full" style={{
        background: s === "new" ? "#0A9B5F" : s === "contacted" ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.05)"
      }} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="font-display font-semibold text-base text-hx-text truncate">
            {contact.name}
          </div>
          <span className="text-hx-muted text-sm truncate">· {contact.company}</span>
          <span className={`text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded border ${badge.cls}`}>
            {badge.label}
          </span>
        </div>
        <div className="mt-1.5 flex items-center gap-4 text-[11px] uppercase tracking-[0.18em] text-hx-muted">
          <span className="inline-flex items-center gap-1.5"><Building2 size={11} /> {contact.sector}</span>
          <span className="inline-flex items-center gap-1.5"><MapPin size={11} /> {contact.country}</span>
          <span className="ml-auto font-mono normal-case tracking-normal text-[10px] text-hx-muted/70">
            {formatDate(contact.created_at)}
          </span>
        </div>
      </div>

      <div className="hidden lg:flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
        {s !== "contacted" && (
          <button onClick={() => onMark(contact.id, "contacted")} data-testid={`mark-contacted-${contact.id}`}
                  className="text-[10px] uppercase tracking-[0.2em] text-hx-muted hover:text-hx-neon px-2 py-1">
            Contacted
          </button>
        )}
        {s !== "archived" && (
          <button onClick={() => onMark(contact.id, "archived")} data-testid={`mark-archived-${contact.id}`}
                  className="text-[10px] uppercase tracking-[0.2em] text-hx-muted hover:text-hx-text px-2 py-1">
            Archive
          </button>
        )}
      </div>
    </div>
  );
};

const DetailDrawer = ({ contact, onClose, onUpdate, onDelete }) => {
  const s = contact.status || "new";
  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
        onClick={onClose}
      />
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="fixed inset-y-0 right-0 w-full sm:w-[480px] hx-glass-strong border-l border-white/[0.10] z-50 overflow-y-auto"
        data-testid="contact-detail-drawer"
      >
        <div className="p-7 lg:p-10">
          <button
            onClick={onClose}
            data-testid="drawer-close"
            className="text-[10px] uppercase tracking-[0.24em] text-hx-muted hover:text-hx-neon transition-colors inline-flex items-center gap-2"
          >
            <ArrowLeft size={12} /> Back
          </button>

          <div className="mt-7">
            <div className="text-[10px] uppercase tracking-[0.22em] text-hx-muted">{contact.sector}</div>
            <h2 className="mt-2 font-display font-bold text-3xl tracking-tight text-hx-text">{contact.name}</h2>
            <div className="mt-1 text-base text-hx-muted">{contact.company}</div>
          </div>

          <div className="mt-8 space-y-4">
            <DetailRow icon={MapPin} label="Country" value={contact.country} />
            {contact.email && (
              <DetailRow
                icon={Mail}
                label="Email"
                value={<a href={`mailto:${contact.email}`} className="text-hx-neon hover:underline">{contact.email}</a>}
              />
            )}
            <DetailRow icon={MessageSquare} label="Message" value={
              <p className="text-sm text-hx-text leading-relaxed whitespace-pre-wrap">{contact.message}</p>
            } />
          </div>

          <div className="mt-8 pt-6 border-t border-white/[0.10]">
            <div className="text-[10px] uppercase tracking-[0.22em] text-hx-muted mb-3">Update status</div>
            <div className="flex flex-wrap gap-2">
              {["new", "contacted", "archived"].map((opt) => (
                <button
                  key={opt}
                  onClick={() => onUpdate(contact.id, opt)}
                  data-testid={`drawer-status-${opt}`}
                  disabled={s === opt}
                  className={`text-[10px] uppercase tracking-[0.22em] px-3 py-2 rounded-md border transition-all ${
                    s === opt
                      ? "border-hx-neon/40 bg-hx-neon/[0.08] text-hx-neon cursor-default"
                      : "border-white/[0.10] bg-white/[0.03] text-hx-muted hover:border-white/25 hover:text-hx-text"
                  }`}
                >
                  {opt === "new" ? "Mark New" : opt === "contacted" ? <><CheckCircle2 size={11} className="inline mr-1.5" /> Mark Contacted</> : <><Archive size={11} className="inline mr-1.5" /> Archive</>}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-white/[0.10]">
            <button
              onClick={() => onDelete(contact.id)}
              data-testid="drawer-delete"
              className="text-[10px] uppercase tracking-[0.22em] text-red-400/80 hover:text-red-300 inline-flex items-center gap-2 transition-colors"
            >
              <Trash2 size={12} /> Delete permanently
            </button>
          </div>

          <div className="mt-10 text-[9px] uppercase tracking-[0.24em] text-hx-muted/70 font-mono">
            ID: {contact.id}
            <br />
            Received: {formatDate(contact.created_at, true)}
          </div>
        </div>
      </motion.div>
    </>
  );
};

const DetailRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="w-8 h-8 rounded-md hx-metal flex items-center justify-center shrink-0">
      <Icon size={13} className="text-hx-neon" strokeWidth={1.5} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-[0.22em] text-hx-muted">{label}</div>
      <div className="mt-1">{typeof value === "string" ? <span className="text-sm text-hx-text">{value}</span> : value}</div>
    </div>
  </div>
);

function formatDate(d, withTime = false) {
  if (!d) return "—";
  const date = typeof d === "string" ? new Date(d) : d;
  if (isNaN(date.getTime())) return "—";
  if (withTime) {
    return date.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  }
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}
